
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ProfilePictureUploader } from "@/components/profile/ProfilePictureUploader";
import { UserProfileForm } from "@/components/profile/UserProfileForm";
import { SubscriptionManagement } from "@/components/profile/SubscriptionManagement";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserCircle, CreditCard, Settings } from "lucide-react";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const Profile = () => {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        navigate("/auth");
        return;
      }
      
      setUser(session.user);
      
      try {
        setLoading(true);
        
        // Fetch user profile
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .maybeSingle();
          
        if (error) throw error;
        
        if (data) {
          setProfile(data);
        }
      } catch (error: any) {
        console.error("Error fetching profile:", error.message);
        toast({
          title: "Error fetching profile",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    getUser();
  }, [navigate, toast]);

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto p-4 mt-8 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="container max-w-4xl mx-auto p-4 pt-8 pb-16">
      <h1 className="text-3xl font-bold mb-8 text-center sm:text-left">Your Profile</h1>
      
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="mb-8 w-full max-w-md mx-auto grid grid-cols-3">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <UserCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="subscription" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Subscription</span>
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Settings</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Profile Picture</CardTitle>
            </CardHeader>
            <CardContent>
              <ProfilePictureUploader 
                user={user} 
                profile={profile} 
                onProfileUpdate={(updatedProfile) => setProfile(updatedProfile)} 
              />
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Personal Information</CardTitle>
            </CardHeader>
            <CardContent>
              <UserProfileForm 
                user={user} 
                profile={profile} 
                onProfileUpdate={(updatedProfile) => setProfile(updatedProfile)} 
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Subscription Management</CardTitle>
            </CardHeader>
            <CardContent>
              <SubscriptionManagement user={user} />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Account Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Email</h3>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
              
              <div>
                <h3 className="font-medium mb-2">Account Actions</h3>
                <Button
                  variant="destructive"
                  onClick={async () => {
                    try {
                      await supabase.auth.signOut();
                      navigate("/");
                    } catch (error: any) {
                      toast({
                        title: "Error signing out",
                        description: error.message,
                        variant: "destructive",
                      });
                    }
                  }}
                  className="w-full sm:w-auto"
                >
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Profile;
